import {
  ExtensionTag,
  NodeExtension,
  type NodeExtensionSpec,
  getActiveNode,
  lift,
  toggleWrap,
  wrapIn,
} from "remirror";
import type { CommandFunction, ProsemirrorNode } from "remirror";

type AlignmentDirection = "left" | "center" | "right";

type AlignmentExtensionOptions = { directions?: AlignmentDirection[] };

const isAlignmentDirection = (
  value: string,
  directions: AlignmentDirection[],
): value is AlignmentDirection =>
  directions.includes(value as AlignmentDirection);

export class AlignmentExtension extends NodeExtension {
  name = "alignment";

  tags = [ExtensionTag.BlockNode];

  defaultOptions: { directions: AlignmentDirection[] } = {
    directions: ["left", "center", "right"],
  };

  constructor(options: AlignmentExtensionOptions = {}) {
    super(options);
  }

  createNodeSpec(extra: {
    defaults: () => Record<string, unknown>;
    parse: (el: Node) => Record<string, unknown>;
    dom: (node: ProsemirrorNode) => Record<string, unknown>;
  }): NodeExtensionSpec {
    const { directions } = this.defaultOptions;

    return {
      content: "block*",
      draggable: false,
      attrs: { ...extra.defaults(), direction: "left" },
      parseDOM: [
        {
          tag: "p",
          priority: 105,
          getAttrs: (el: Node) => {
            const element = el as HTMLElement;
            const textAlign = element.style?.textAlign ?? "";
            if (!isAlignmentDirection(textAlign, directions)) {
              return false;
            }
            return { ...extra.parse(el), direction: textAlign };
          },
        },
        {
          tag: "div",
          priority: 45,
          getAttrs: (el: Node) => {
            const element = el as HTMLElement;
            const textAlign = element.style?.textAlign ?? "";
            if (!isAlignmentDirection(textAlign, directions)) {
              return false;
            }
            return { ...extra.parse(el), direction: textAlign };
          },
        },
      ],
      toDOM: (node: ProsemirrorNode) => [
        "div",
        {
          ...extra.dom(node),
          style: `text-align: ${node.attrs.direction as string};`,
        },
        0,
      ],
    };
  }

  createCommands() {
    // @ts-expect-error remirror node type is set at runtime by NodeExtension
    const { type } = this;

    return {
      toggleAlignment: (attrs: { direction: AlignmentDirection }) =>
        toggleWrap(type, attrs),
      removeAlignment: () => (param: CommandFunction) => {
        const { tr } = param;
        const activeNode = getActiveNode({ state: tr, type });
        if (activeNode) {
          return lift(param);
        }
        return false;
      },
      setAlignment:
        ({ direction }: { direction: AlignmentDirection }) =>
        (param: CommandFunction) => {
          const { tr } = param;
          const activeNode = getActiveNode({ state: tr, type });
          if (activeNode) {
            const lifted = lift(param);
            if (activeNode.node.attrs.direction === direction) {
              return lifted;
            }
          }
          return wrapIn(type, { direction })(param);
        },
    };
  }
}

export const createAlignmentExtension = (
  options: Array<AlignmentExtensionOptions>,
) => new AlignmentExtension(options[0]);
